import { AccountAvatar } from "@/components/AccountMenu";
import { PageHeader } from "@/components/PageHeader";
import {
  SettingsCard,
  SettingsDialog,
  SettingsError,
  SettingsSection,
} from "@/components/SettingsUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type KekProvider,
  type Me,
  type OrgRecord,
  type UpdateMeBody,
  type UpdateOrgBody,
  WardkeyAPIError,
  displayName,
  useActiveOrg,
  useApiMutation,
  useApiQuery,
  useOrg,
} from "@/lib/api";
import { useToast } from "@/lib/toast";
import {
  ArrowUpRight,
  Building2,
  CreditCard,
  Fingerprint,
  History,
  KeyRound,
  LockKeyhole,
  type LucideIcon,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UserRound,
  Users,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useOutletContext, useRevalidator } from "react-router";

type SettingsTab = { to: string; label: string; icon: LucideIcon; end?: boolean; danger?: boolean };
const groups: { label: string; tabs: SettingsTab[] }[] = [
  { label: "Personal", tabs: [{ to: "/settings", end: true, label: "Profile", icon: UserRound }] },
  {
    label: "Organization",
    tabs: [
      { to: "/settings/organization", label: "Organization", icon: Building2 },
      { to: "/settings/billing", label: "Billing", icon: CreditCard },
      { to: "/settings/security", label: "Security", icon: ShieldCheck },
      { to: "/settings/service-tokens", label: "Service tokens", icon: KeyRound },
      { to: "/settings/audit", label: "Audit", icon: History },
      { to: "/settings/danger", label: "Danger zone", icon: Trash2, danger: true },
    ],
  },
];

export function SettingsLayout() {
  const me = useOutletContext<Me>();
  const activeOrg = useActiveOrg();
  return (
    <div className="settings-page">
      <PageHeader
        title="Settings"
        subtitle="Make yourself at home. Manage your account and workspace."
      />
      <div className="settings-layout">
        <nav aria-label="Settings sections" className="settings-nav">
          {groups.map((group) => (
            <div className="settings-nav-group" key={group.label}>
              <p className="settings-nav-label">{group.label}</p>
              {group.tabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end ?? false}
                  className={({ isActive }) =>
                    `settings-tab${isActive ? " active" : ""}${tab.danger ? " danger" : ""}`
                  }
                >
                  <tab.icon size={16} aria-hidden="true" />
                  <span>{tab.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
          <div className="settings-workspace">
            <Building2 size={16} aria-hidden="true" />
            <div>
              <strong title={activeOrg.name}>{activeOrg.name}</strong>
              <span>{activeOrg.role} access</span>
            </div>
          </div>
        </nav>
        <div className="settings-content" key={activeOrg.org_id}>
          <Outlet context={me} />
        </div>
      </div>
    </div>
  );
}

export function SettingsProfileRoute() {
  const me = useOutletContext<Me>();
  return (
    <SettingsSection
      title="Your profile"
      description="The personal details you bring to every workspace."
      icon={UserRound}
    >
      <SettingsCard
        title="Personal information"
        description="Choose how your name appears to teammates."
      >
        <div className="settings-identity">
          <AccountAvatar me={me} className="settings-profile-monogram" />
          <div>
            <strong>{displayName(me)}</strong>
            <span>{me.email}</span>
          </div>
          <span className="settings-badge">via {me.provider}</span>
        </div>
        <DisplayNameForm initialName={me.name ?? ""} email={me.email} />
      </SettingsCard>
      <SettingsCard title="Sign-in method" description="The account you use to access Wardkey.">
        <div className="settings-info-row">
          <Fingerprint size={20} aria-hidden="true" />
          <div>
            <strong>{providerLabel(me.provider)}</strong>
            <p>Email and sign-in method changes aren't available in the console.</p>
          </div>
        </div>
      </SettingsCard>
    </SettingsSection>
  );
}

function DisplayNameForm({ initialName, email }: { initialName: string; email: string }) {
  const { pushToast } = useToast();
  const { revalidate } = useRevalidator();
  const [error, setError] = useState<string | null>(null);
  const update = useApiMutation<UpdateMeBody, unknown>((client, body) => client.me.update(body), {
    suppressToast: true,
    onSuccess: () => {
      pushToast({ kind: "success", title: "Profile updated" });
      revalidate();
    },
    onError: (err) => setError(err.message),
  });
  return (
    <NameForm
      label="Display name"
      initialName={initialName}
      max={80}
      allowEmpty
      placeholder={email.split("@")[0] ?? email}
      hint="Shown in member lists and audit entries. Leave blank to use your email name."
      saveLabel="Save profile"
      busy={update.isPending}
      error={error}
      onEdit={() => setError(null)}
      onSave={(name) => {
        setError(null);
        update.mutate({ name: name || null });
      }}
    />
  );
}

function NameForm({
  label,
  initialName,
  max,
  allowEmpty = false,
  placeholder,
  hint,
  saveLabel,
  busy,
  error,
  onEdit,
  onSave,
}: {
  label: string;
  initialName: string;
  max: number;
  allowEmpty?: boolean;
  placeholder?: string;
  hint: string;
  saveLabel: string;
  busy: boolean;
  error: string | null;
  onEdit: () => void;
  onSave: (name: string) => void;
}) {
  const id = useId();
  const [name, setName] = useState(initialName);
  useEffect(() => setName(initialName), [initialName]);
  const changed = name.trim() !== initialName.trim();
  const valid = name.trim().length <= max && (allowEmpty || name.trim().length > 0);
  return (
    <form
      className="settings-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (changed && valid && !busy) onSave(name.trim());
      }}
    >
      <div className="settings-field">
        <label htmlFor={id}>{label}</label>
        <Input
          id={id}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            onEdit();
          }}
          maxLength={max}
          placeholder={placeholder}
          disabled={busy}
          autoComplete={allowEmpty ? "name" : "organization"}
          aria-invalid={!!error || !valid}
          aria-describedby={`${id}-hint`}
        />
        <p id={`${id}-hint`}>{hint}</p>
      </div>
      {error && (
        <p className="settings-inline-error" role="alert">
          {error}
        </p>
      )}
      <div className="settings-form-footer">
        <span>{changed ? "Unsaved changes" : "Your details are up to date"}</span>
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!changed || busy}
            onClick={() => {
              setName(initialName);
              onEdit();
            }}
          >
            Reset
          </Button>
          <Button type="submit" size="sm" disabled={!changed || !valid || busy}>
            {busy ? "Saving…" : saveLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}

function useOrgDetails() {
  const org = useActiveOrg();
  return useApiQuery(["org", org.org_id], (client) => client.orgs.get(org.org_id), {
    suppressToast: true,
  });
}

export function SettingsOrganizationRoute() {
  const activeOrg = useActiveOrg();
  const org = useOrgDetails();
  return (
    <SettingsSection
      title="Organization"
      description="The shared home for your projects, secrets, and team."
      icon={Building2}
    >
      {org.error ? (
        <SettingsError error={org.error} retry={org.refetch} />
      ) : !org.data || org.isLoading ? (
        <SettingsLoading />
      ) : (
        <>
          <SettingsCard
            title="Workspace details"
            description="A recognizable name makes switching workspaces easier."
          >
            <OrgNameField
              key={org.data.org_id}
              orgId={org.data.org_id}
              initialName={org.data.name}
              canEdit={activeOrg.role === "owner" || activeOrg.role === "admin"}
              onRenamed={org.refetch}
            />
          </SettingsCard>
          <SettingsCard title="Organization details">
            <dl className="settings-detail-grid">
              <Field label="Organization ID" value={org.data.org_id} mono />
              <Field label="Created" value={formatDate(org.data.created_at)} />
              <Field
                label="Plan"
                value={<span className="settings-badge capitalize">{org.data.plan}</span>}
              />
              <Field
                label="Encryption key"
                value={kekLabel(org.data.kek_provider)}
                hint={kekHint(org.data.kek_provider)}
              />
            </dl>
          </SettingsCard>
          <Link to="/members" className="settings-linked-card">
            <Users size={20} aria-hidden="true" />
            <span>
              <strong>People in your workspace</strong>
              <small>Manage members, invitations, and roles.</small>
            </span>
            <ArrowUpRight size={17} aria-hidden="true" />
          </Link>
        </>
      )}
    </SettingsSection>
  );
}

function OrgNameField({
  orgId,
  initialName,
  canEdit,
  onRenamed,
}: { orgId: string; initialName: string; canEdit: boolean; onRenamed: () => void }) {
  const { pushToast } = useToast();
  const { refresh } = useOrg();
  const [error, setError] = useState<string | null>(null);
  const update = useApiMutation<UpdateOrgBody, OrgRecord>(
    (client, body) => client.orgs.update(orgId, body),
    {
      suppressToast: true,
      onSuccess: (record) => {
        pushToast({ kind: "success", title: `Renamed to ${record.name}` });
        refresh();
        onRenamed();
      },
      onError: (err) => setError(err.message),
    },
  );
  if (!canEdit)
    return (
      <dl className="settings-detail-grid">
        <Field
          label="Organization name"
          value={initialName}
          hint="Owner or admin role required to rename"
        />
      </dl>
    );
  return (
    <NameForm
      label="Organization name"
      initialName={initialName}
      max={64}
      hint="Visible to everyone in this organization."
      saveLabel="Save name"
      busy={update.isPending}
      error={error}
      onEdit={() => setError(null)}
      onSave={(name) => {
        setError(null);
        update.mutate({ name });
      }}
    />
  );
}

function Field({
  label,
  value,
  hint,
  mono = false,
}: { label: string; value: ReactNode; hint?: string; mono?: boolean }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? "settings-mono" : undefined}>
        {value}
        {hint && <small>{hint}</small>}
      </dd>
    </div>
  );
}
function SettingsLoading() {
  return (
    <div className="settings-card settings-loading" aria-busy="true" aria-label="Loading settings">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}
function providerLabel(provider: Me["provider"]) {
  return { github: "GitHub", google: "Google", email: "Email magic link" }[provider];
}
function kekLabel(provider: KekProvider) {
  return provider === "software"
    ? "software"
    : provider === "per_tenant_cmk"
      ? "Customer-managed key"
      : "External key store";
}
function kekHint(provider: KekProvider) {
  return provider === "software"
    ? "Keys managed by Wardkey."
    : provider === "per_tenant_cmk"
      ? "A dedicated customer-managed key in AWS KMS."
      : "An external key store backed by your HSM.";
}

export function SettingsBillingRoute() {
  const org = useOrgDetails();
  return (
    <SettingsSection
      title="Plan and billing"
      description="Your workspace plan and billing details, in one place."
      icon={CreditCard}
    >
      {org.error ? (
        <SettingsError error={org.error} retry={org.refetch} />
      ) : !org.data || org.isLoading ? (
        <SettingsLoading />
      ) : (
        <SettingsCard title="Current plan">
          <div className="settings-plan">
            <span className="settings-section-icon">
              <CreditCard size={23} aria-hidden="true" />
            </span>
            <div>
              <strong>{org.data.plan}</strong>
              <p>Current plan for {org.data.name}</p>
            </div>
            <span className="settings-badge">Current</span>
          </div>
        </SettingsCard>
      )}
      <UpcomingCard
        title="Billing management"
        description="Self-service billing isn't available in the console yet."
        features={[
          { title: "Plan changes", description: "Compare plans and manage your subscription." },
          {
            title: "Payment details",
            description: "Update a payment method and billing information.",
          },
          { title: "Invoices", description: "Find and download your billing history." },
        ]}
      />
    </SettingsSection>
  );
}

export function SettingsSecurityRoute() {
  const me = useOutletContext<Me>();
  const org = useOrgDetails();
  return (
    <SettingsSection
      title="Security policy"
      description="Understand how you sign in and how workspace keys are managed."
      icon={ShieldCheck}
    >
      <SettingsCard title="Your sign-in">
        <div className="settings-info-row">
          <Fingerprint size={20} aria-hidden="true" />
          <div>
            <strong>{providerLabel(me.provider)}</strong>
            <p>{me.email}</p>
          </div>
          <span className="settings-badge">Current method</span>
        </div>
      </SettingsCard>
      {org.error ? (
        <SettingsError error={org.error} retry={org.refetch} />
      ) : !org.data || org.isLoading ? (
        <SettingsLoading />
      ) : (
        <SettingsCard title="Workspace encryption">
          <div className="settings-info-row">
            <LockKeyhole size={20} aria-hidden="true" />
            <div>
              <strong>{kekLabel(org.data.kek_provider)}</strong>
              <p>{kekHint(org.data.kek_provider)}</p>
            </div>
          </div>
        </SettingsCard>
      )}
      <UpcomingCard
        title="Organization policies"
        description="These policies aren't configurable in the console yet."
        features={[
          {
            title: "Additional verification",
            description: "Manage verification requirements for sensitive actions.",
          },
          { title: "Session policies", description: "Set session lifetime for your organization." },
          { title: "Network access", description: "Manage IP allowlists for API access." },
        ]}
      />
    </SettingsSection>
  );
}

export function SettingsAuditRoute() {
  return (
    <SettingsSection
      title="Audit and alerting"
      description="Keep track of workspace activity and decide how to stay informed."
      icon={History}
      badge="Coming soon"
    >
      <UpcomingCard
        title="A clearer view of workspace activity"
        description="Audit configuration and alert delivery aren't available in the console yet."
        features={[
          {
            title: "Retention",
            description: "Choose how long to retain your organization's activity history.",
          },
          {
            title: "Alert destinations",
            description: "Choose where workspace alerts are delivered.",
          },
          { title: "Activity rules", description: "Configure which events need your attention." },
        ]}
      />
    </SettingsSection>
  );
}

function UpcomingCard({
  title,
  description,
  features,
}: { title: string; description: string; features: { title: string; description: string }[] }) {
  return (
    <SettingsCard title={title} description={description} className="settings-upcoming">
      <div className="settings-availability">
        <SlidersHorizontal size={16} aria-hidden="true" />
        <span>Coming soon</span>
      </div>
      <dl className="settings-feature-list">
        {features.map((feature) => (
          <div key={feature.title}>
            <dt>{feature.title}</dt>
            <dd>{feature.description}</dd>
          </div>
        ))}
      </dl>
    </SettingsCard>
  );
}

export function SettingsDangerRoute() {
  const activeOrg = useActiveOrg();
  const canDelete = activeOrg.role === "owner";
  const [confirming, setConfirming] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  return (
    <SettingsSection
      title="Danger zone"
      description="Permanent changes to your organization. Take a moment before continuing."
      icon={Trash2}
    >
      <SettingsCard
        title="Delete organization"
        description="Permanently remove this workspace and its secrets."
        className="settings-danger-card"
      >
        <div className="settings-danger-body">
          <strong>{activeOrg.name}</strong>
          <p>
            This removes the organization, its service tokens, and encrypted secret material. This
            action cannot be undone.
          </p>
          <ul>
            <li>Export anything you still need before deleting.</li>
            <li>Remove every other member from the organization first.</li>
          </ul>
        </div>
        <div className="settings-form-footer">
          <span>
            {canDelete ? "Only the organization owner can delete it." : "Owner role required."}
          </span>
          <Button
            ref={triggerRef}
            type="button"
            variant="destructive"
            size="sm"
            disabled={!canDelete}
            onClick={() => setConfirming(true)}
          >
            Delete organization
          </Button>
        </div>
      </SettingsCard>
      {confirming && (
        <DeleteOrgModal
          orgId={activeOrg.org_id}
          orgName={activeOrg.name}
          onClose={() => setConfirming(false)}
          returnFocus={triggerRef}
        />
      )}
    </SettingsSection>
  );
}

function DeleteOrgModal({
  orgId,
  orgName,
  onClose,
  returnFocus,
}: {
  orgId: string;
  orgName: string;
  onClose: () => void;
  returnFocus: React.RefObject<HTMLButtonElement | null>;
}) {
  const { pushToast } = useToast();
  const { refresh } = useOrg();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const remove = useApiMutation<void, unknown>((client) => client.orgs.delete(orgId), {
    suppressToast: true,
    onSuccess: () => {
      pushToast({ kind: "success", title: `Deleted ${orgName}` });
      refresh();
      navigate("/");
    },
    onError: (err) =>
      setError(
        err instanceof WardkeyAPIError && err.status === 409
          ? "Remove every other member first, then try again."
          : err.message,
      ),
  });
  const matches = confirmText === orgName;
  return (
    <SettingsDialog
      title="Delete this organization?"
      description={`Deleting ${orgName} permanently removes its tokens and encrypted secrets. This cannot be undone.`}
      onClose={onClose}
      busy={remove.isPending}
      returnFocus={returnFocus}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (matches && !remove.isPending) {
            setError(null);
            remove.mutate();
          }
        }}
      >
        <div className="settings-field">
          <label htmlFor="delete-org-confirm">
            Type <strong>{orgName}</strong> to confirm
          </label>
          <Input
            id="delete-org-confirm"
            aria-label="Org name to confirm"
            value={confirmText}
            onChange={(event) => {
              setConfirmText(event.target.value);
              setError(null);
            }}
            disabled={remove.isPending}
            autoComplete="off"
            spellCheck={false}
            placeholder={orgName}
            aria-invalid={!!error}
          />
        </div>
        {error && (
          <p className="settings-inline-error" role="alert">
            {error}
          </p>
        )}
        <div className="settings-dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" disabled={!matches || remove.isPending}>
            {remove.isPending ? "Deleting…" : "Delete organization"}
          </Button>
        </div>
      </form>
    </SettingsDialog>
  );
}

function formatDate(ts: number) {
  const date = new Date(ts < 1e12 ? ts * 1000 : ts);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
